import React, { useRef, useState } from "react";
import { FiMessageCircle, FiPause, FiPlay, FiSend, FiTrash2 } from "react-icons/fi";
import "./Guestbook.css";

const MAX_LENGTH = 80;
const MAX_NOTES = 24;
const SPEED = 85;

function Guestbook() {
  const wallRef = useRef(null);
  const measureRef = useRef(null);
  const inputRef = useRef(null);
  const lanesRef = useRef([]);
  const pausedAtRef = useRef(0);
  const nextIdRef = useRef(0);
  const [text, setText] = useState("");
  const [notes, setNotes] = useState([]);
  const [paused, setPaused] = useState(false);
  const [hasSent, setHasSent] = useState(false);
  const [status, setStatus] = useState("");

  function sendNote(event) {
    event.preventDefault();
    const message = text.trim();
    if (!message || paused) return;
    if (notes.length >= MAX_NOTES) {
      setStatus("The wall is busy. Let a few notes float by, then try again.");
      return;
    }

    const wall = wallRef.current;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const laneHeight = reducedMotion ? 100 : 54;
    const laneCount = Math.max(1, Math.floor((wall.clientHeight - 64) / laneHeight));
    const now = Date.now();
    const lanes = Array.from({ length: laneCount }, (_, index) => lanesRef.current[index] || 0);
    const earliest = Math.min(...lanes);
    const lane = lanes.indexOf(earliest);
    const startsAt = Math.max(now, earliest);

    measureRef.current.textContent = message;
    const noteWidth = measureRef.current.getBoundingClientRect().width || message.length * 14 + 48;
    measureRef.current.textContent = "";
    const travel = (wall.clientWidth || 640) + noteWidth + 32;
    const duration = reducedMotion ? 7 : travel / SPEED;
    // Give each note enough room before the next one enters the same lane.
    lanes[lane] = startsAt + (reducedMotion ? duration : (noteWidth + 40) / SPEED) * 1000;
    lanesRef.current = lanes;
    const note = {
      id: ++nextIdRef.current,
      message,
      lane,
      top: 32 + lane * laneHeight,
      delay: (startsAt - now) / 1000,
      duration,
      travel,
    };
    setNotes((current) => [...current, note]);
    setText("");
    setHasSent(true);
    setStatus("Sent! Your note is floating across the wall.");
    inputRef.current.focus();
  }

  function togglePause() {
    if (paused) {
      const elapsed = Date.now() - pausedAtRef.current;
      lanesRef.current = lanesRef.current.map((time) => time + elapsed);
    } else {
      pausedAtRef.current = Date.now();
    }
    setPaused((value) => !value);
  }

  function clearWall() {
    setNotes([]);
    lanesRef.current = [];
    setPaused(false);
    setStatus("Wall cleared. Send another hello whenever you like.");
  }

  return (
    <main className="guestbook-section">
      <div className="guestbook-container">
        <header className="guestbook-intro">
          <p className="guestbook-eyebrow">Guestbook / A little connection</p>
          <h1>A thought. A question.<br /><span>Or just a hello.</span></h1>
          <p>A small space to leave a note and watch it drift by.</p>
        </header>

        <section className="guestbook-wall-section" aria-label="Message wall">
          <div className="guestbook-wall-toolbar">
            <span className="guestbook-wall-label"><span aria-hidden="true" />Message wall</span>
            <div className="guestbook-wall-actions">
              <button type="button" onClick={togglePause} disabled={!notes.length} aria-pressed={paused}>
                {paused ? <FiPlay aria-hidden="true" /> : <FiPause aria-hidden="true" />}
                {paused ? "Resume" : "Pause"}
              </button>
              <button type="button" onClick={clearWall} disabled={!notes.length}>
                <FiTrash2 aria-hidden="true" />Clear
              </button>
            </div>
          </div>

          <div className="guestbook-wall" ref={wallRef} aria-label="Floating messages">
            {!notes.length && (
              <div className="guestbook-empty">
                <FiMessageCircle aria-hidden="true" />
                <h2>{hasSent ? "Room for another hello." : "Make the first move."}</h2>
                <p>Your words could be the next ones floating by.</p>
              </div>
            )}
            {notes.map((note) => (
              <div
                key={note.id}
                className={`guestbook-note guestbook-note--${note.lane % 3}`}
                style={{
                  top: note.top,
                  "--note-travel": `${note.travel}px`,
                  animationDelay: `${note.delay}s`,
                  animationDuration: `${note.duration}s`,
                  animationPlayState: paused ? "paused" : "running",
                }}
                onAnimationEnd={(event) => {
                  if (event.target === event.currentTarget) {
                    setNotes((current) => current.filter((item) => item.id !== note.id));
                  }
                }}
              >
                <span className="guestbook-bubble">{note.message}</span>
              </div>
            ))}
            <span ref={measureRef} className="guestbook-bubble guestbook-measure" aria-hidden="true" />
          </div>

          <form className="guestbook-form" onSubmit={sendNote} aria-label="Send a message">
            <label htmlFor="guestbook-message">Send a note across the screen</label>
            <div className="guestbook-compose">
              <input
                id="guestbook-message"
                ref={inputRef}
                value={text}
                onChange={(event) => setText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && event.nativeEvent.isComposing) event.preventDefault();
                }}
                maxLength={MAX_LENGTH}
                placeholder="Say something kind, curious, or unexpected…"
                autoComplete="off"
                aria-describedby="guestbook-hint"
                required
              />
              <button type="submit" disabled={!text.trim() || paused || notes.length >= MAX_NOTES}>
                Send <FiSend aria-hidden="true" />
              </button>
            </div>
            <div className="guestbook-form-meta" id="guestbook-hint">
              <span>{paused ? "Resume the wall to send your note." : "Just for this visit. Notes disappear as they drift away."}</span>
              <span>{text.length}/{MAX_LENGTH}</span>
            </div>
          </form>
        </section>
        <p className="guestbook-status" role="status">{status}</p>
      </div>
    </main>
  );
}

export default Guestbook;
